import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  TextField,
  Button,
  Pagination,
  Snackbar,
  Alert,
  FormControl,
  Select,
  MenuItem,
  Stack,
  Tooltip,
  Autocomplete,
  Paper,
  Popover,
  InputLabel,
  Card,
  CardContent,
  Chip,
  Avatar,
  Dialog,
  Divider,
  IconButton,
} from '@mui/material';
import { FileDownload, Refresh as RefreshIcon, CalendarMonth, Close, Login, Logout, Search as SearchIcon, PhotoLibrary as PhotoLibraryIcon } from '@mui/icons-material';
import { Fade, useMediaQuery, useTheme } from '@mui/material';
import { useLocation, useSearchParams } from 'react-router-dom';
import PageHeader from './PageHeader';
import ClearAllFiltersButton from './ClearAllFiltersButton';
import {
  pageFilterAutocompleteSx,
  pageFilterDatePickerSx,
  pageFilterFormControlSx,
  pageFiltersControlsSx,
  pageFiltersStackSx,
  pageFiltersToolbarSx,
} from '../utils/pageFilterStyles';
import {
  createTodayDateRange,
  DEFAULT_END_TIME,
  DEFAULT_START_TIME,
  isDefaultDateTimeRange,
} from '../utils/filterDefaults';
import { DateRangePicker } from 'react-date-range';
import { format, addDays, startOfDay, endOfDay, isSameDay } from 'date-fns';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

import { getAuditLogs, exportAuditLogs, getUniqueAuditNames, getPersonFaces, getFaceImage, getAuditImageBlob, getAllPersonNames } from '../utils/api';
import { hydrateTimingDialogFromDetail } from '../utils/auditTimingDialog';
import { peek as peekAuditImage, subscribe as subscribeAuditImage, getOrFetch as getOrFetchAuditImage } from '../services/auditImageCache';
import LazyAuditImg from './LazyAuditImg';
import AuditImg from './AuditImg';
import { AttendanceCardSkeleton } from './Audit';
import MissingEntrySearchModal from './MissingEntrySearchModal';
import { DayTimelineBar, buildAttendanceCapturedImageGallery } from './AuditAttendanceGridLayout';
import { useImagePreview } from './ImagePreviewDialog';
import { formatDate, formatDateOnly } from '../utils/dateUtils';
import { sanitizeNameSearchInput } from '../utils/nameSearchInput';

const primary = '#40518A';
const ANONYMOUS_IMAGE = '/anonymous.webp';

const resolveIntrusionEventImage = (event, snapshotCache, fallbackImage = null) => {
  if (!event) return fallbackImage || ANONYMOUS_IMAGE;
  if (event.is_manual || event.is_missing_entry) return ANONYMOUS_IMAGE;
  if (event.snapshot_audit_id) {
    const cached = snapshotCache?.[event.snapshot_audit_id] ?? peekAuditImage(event.snapshot_audit_id);
    if (cached !== undefined && cached !== null) return cached;
  }
  if (event.image_available && event.attendance_id && snapshotCache) {
    const key = `attendance:${event.attendance_id}`;
    if (snapshotCache[key]) return snapshotCache[key];
  }
  return fallbackImage || ANONYMOUS_IMAGE;
};

const getTimelineEventKey = (event) => {
  if (!event) return '';
  return [
    event.snapshot_audit_id ? `audit:${event.snapshot_audit_id}` : 'audit:none',
    event.attendance_id ? `attendance:${event.attendance_id}` : 'attendance:none',
    `action:${event.action || 'evt'}`,
    `at:${event.created_at || ''}`,
  ].join('|');
};

const collectIntrusionGalleryAuditIds = (events = []) => (
  Array.from(
    new Set(
      (Array.isArray(events) ? events : [])
        .map((event) => {
          const raw = event?.snapshot_audit_id || event?.audit_id || event?.recognition_audit_log?.audit_id;
          return raw != null ? String(raw).trim() : '';
        })
        .filter(Boolean),
    ),
  )
);

const openIntrusionCapturedImageGallery = async ({
  openImagePreview,
  summary,
  snapshotCache,
  thumbnail = '',
  startEvent = null,
  startSrc = null,
}) => {
  if (typeof openImagePreview !== 'function' || !summary) return;

  const auditIds = collectIntrusionGalleryAuditIds(summary.recent_events);
  if (auditIds.length) {
    await Promise.all(auditIds.map((auditId) => getOrFetchAuditImage(auditId)));
  }

  const images = buildAttendanceCapturedImageGallery({ summary, snapshotCache });
  if (!images.length) return;

  let index = 0;
  if (startSrc) {
    const bySrc = images.findIndex((image) => image.src === startSrc);
    if (bySrc >= 0) index = bySrc;
  } else if (startEvent) {
    const eventSrc = resolveIntrusionEventImage(startEvent, snapshotCache, thumbnail || null);
    const byEvent = images.findIndex((image) => image.src === eventSrc);
    if (byEvent >= 0) index = byEvent;
  }

  const selected = images[index] || images[0];
  openImagePreview({
    src: selected.src,
    title: selected.title || 'Captured frame',
    alt: selected.alt || selected.title || 'Captured frame',
    images,
    index,
    employeeName: summary.person_name || 'Unknown person',
    employeeId: summary.employee_code || summary.person_id || '',
    employeeImage: thumbnail || '',
  });
};

const calculatePresenceFromEvents = (events = []) => {
  if (!Array.isArray(events) || events.length === 0) return '0m';
  const sorted = [...events].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  let lastIn = null;
  let totalMinutes = 0;

  sorted.forEach((evt) => {
    const eventTime = new Date(evt.created_at);
    if (Number.isNaN(eventTime.getTime())) return;
    if (evt.action === 'check_in') {
      lastIn = eventTime;
    } else if (evt.action === 'check_out' && lastIn) {
      totalMinutes += Math.max(0, Math.floor((eventTime - lastIn) / (1000 * 60)));
      lastIn = null;
    }
  });

  if (lastIn) {
    const now = new Date();
    totalMinutes += Math.max(0, Math.floor((now - lastIn) / (1000 * 60)));
  }

  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
};

const IntrusionDayTimelineBar = ({ date, events = [], isToday = false, snapshotCache = {}, highlightEventKey = null }) => (
  <DayTimelineBar
    date={date}
    events={events}
    isToday={isToday}
    snapshotCache={snapshotCache}
    highlightEventKey={highlightEventKey}
    hideSummary={true}
  />
);

const IntrusionTimelineModal = ({ open, onClose, summary, thumbnail, snapshotCache, onAttendanceEventLogClick = null, onMissingEntrySearchClick = null }) => {
  const theme = useTheme();
  const isMobileModal = useMediaQuery(theme.breakpoints.down('sm'));
  const [hoveredTimelineEventKey, setHoveredTimelineEventKey] = useState(null);
  const { openImagePreview } = useImagePreview();

  useEffect(() => {
    if (!open) setHoveredTimelineEventKey(null);
  }, [open]);

  if (!summary) return null;

  const { person_name, person_id, employee_code, total_check_ins = 0, total_check_outs = 0, recent_events = [] } = summary;
  const avatarLetter = (person_name || person_id || '?').charAt(0).toUpperCase();
  const sortedEvents = [...recent_events].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  const presence = calculatePresenceFromEvents(recent_events);
  const modalDateLabel = (() => {
    if (!recent_events.length) return 'No date';
    const byTimeAsc = [...recent_events].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    const firstDate = byTimeAsc[0]?.created_at;
    const lastDate = byTimeAsc[byTimeAsc.length - 1]?.created_at;
    if (!firstDate || !lastDate) return 'No date';
    const firstKey = format(new Date(firstDate), 'yyyy-MM-dd');
    const lastKey = format(new Date(lastDate), 'yyyy-MM-dd');
    if (firstKey === lastKey) return format(new Date(firstDate), 'EEE, MMM dd, yyyy');
    return `${format(new Date(firstDate), 'MMM dd, yyyy')} - ${format(new Date(lastDate), 'MMM dd, yyyy')}`;
  })();

  const groupedDays = (() => {
    const groups = {};
    recent_events.forEach((event) => {
      const eventDate = new Date(event.created_at);
      const dateKey = format(eventDate, 'yyyy-MM-dd');
      const dateLabel = format(eventDate, 'EEE, MMM dd, yyyy');
      if (!groups[dateKey]) groups[dateKey] = { dateKey, dateLabel, events: [] };
      groups[dateKey].events.push(event);
    });
    return Object.values(groups).sort((a, b) => b.dateKey.localeCompare(a.dateKey));
  })();
  const isTodayDate = (dateKey) => format(new Date(), 'yyyy-MM-dd') === dateKey;

  const handleProfileImagePreview = (event) => {
    if (!thumbnail) return;
    event.preventDefault();
    event.stopPropagation();
    openImagePreview({
      src: thumbnail,
      title: person_name || 'Profile image',
      alt: person_name || 'Profile image',
      employeeName: person_name || 'Unknown person',
      employeeId: employee_code || person_id || '',
      employeeImage: thumbnail,
      hideTitle: true,
    });
  };

  const handleImageClick = (event) => {
    if (!event || event.is_missing_entry) return;
    void openIntrusionCapturedImageGallery({
      openImagePreview,
      summary,
      snapshotCache,
      thumbnail,
      startEvent: event,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobileModal}
      TransitionComponent={Fade}
      TransitionProps={{ timeout: 180 }}
      PaperProps={{ sx: { borderRadius: isMobileModal ? 0 : 3, overflow: 'hidden', bgcolor: '#fafbfc', maxHeight: isMobileModal ? '100vh' : '85vh' } }}
    >
      <Box sx={{ p: { xs: 2, sm: 2.5 }, bgcolor: '#fff', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
            <Avatar
              src={thumbnail || undefined}
              alt={person_name || 'Unknown Person'}
              imgProps={{ 'data-no-image-preview': 'true' }}
              onClick={handleProfileImagePreview}
              sx={{
                width: { xs: 44, sm: 56 },
                height: { xs: 44, sm: 56 },
                bgcolor: primary,
                fontWeight: 700,
                cursor: thumbnail ? 'pointer' : 'default',
              }}
            >
              {avatarLetter}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: { xs: '1rem', sm: '1.25rem' } }} noWrap>
                {person_name || 'Unknown Person'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {employee_code || person_id}
              </Typography>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                {/* <Chip size="small" label={`In: ${total_check_ins}`} sx={{ bgcolor: '#dcfce7', color: '#166534', fontWeight: 700 }} />
                <Chip size="small" label={`Out: ${total_check_outs}`} sx={{ bgcolor: '#fef9c3', color: '#854d0e', fontWeight: 700 }} />
                <Chip size="small" label={`Presence: ${presence}`} sx={{ bgcolor: '#dbeafe', color: '#1d4ed8', fontWeight: 700 }} /> */}
                <Chip size="small" label={modalDateLabel} sx={{ bgcolor: '#f1f5f9', color: '#334155', fontWeight: 700 }} />
              </Stack>
            </Box>
          </Stack>
          <IconButton onClick={onClose} size="small" sx={{ bgcolor: '#f1f5f9', '&:hover': { bgcolor: '#e2e8f0' } }}>
            <Close fontSize="small" />
          </IconButton>
        </Stack>
      </Box>

      <Box sx={{ p: { xs: 2, sm: 3 }, overflowY: 'auto', maxHeight: isMobileModal ? 'calc(100vh - 160px)' : 'calc(85vh - 180px)' }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" spacing={{ xs: 1, sm: 0 }} sx={{ mb: 1.75 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
              Daily Timeline
            </Typography>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#ef4444' }} />
          </Stack>
          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
            <Stack direction="row" spacing={0.5} alignItems="center"><Box sx={{ width: 12, height: 6, bgcolor: '#86efac', borderRadius: 0.5 }} /><Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>Present</Typography></Stack>
            <Stack direction="row" spacing={0.5} alignItems="center"><Box sx={{ width: 12, height: 6, bgcolor: '#fecaca', borderRadius: 0.5 }} /><Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>Away</Typography></Stack>
            <Stack direction="row" spacing={0.5} alignItems="center"><Box sx={{ width: 8, height: 8, bgcolor: '#22c55e', borderRadius: '50%' }} /><Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>In</Typography></Stack>
            <Stack direction="row" spacing={0.5} alignItems="center"><Box sx={{ width: 8, height: 8, bgcolor: '#f59e0b', borderRadius: '50%' }} /><Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>Out</Typography></Stack>
          </Stack>
        </Stack>

        {groupedDays.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center', bgcolor: '#fff', borderRadius: 2, border: '1px solid', borderColor: 'divider', mb: 2 }}>
            <Typography variant="body2" color="text.secondary">No activity recorded for this period</Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {groupedDays.map((day) => {
              const daySortedEvents = [...day.events].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
              return (
                <Box key={day.dateKey} sx={{ bgcolor: '#fff', borderRadius: 2, border: '1px solid', borderColor: 'divider', p: 2 }}>
                <IntrusionDayTimelineBar
                  date={day.dateLabel}
                  events={day.events}
                  isToday={isTodayDate(day.dateKey)}
                  snapshotCache={snapshotCache}
                  highlightEventKey={hoveredTimelineEventKey}
                />

                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.25, mt: 0.25 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                      Related Images
                    </Typography>
                    <Chip size="small" label={daySortedEvents.length} sx={{ height: 20, fontSize: '0.65rem', bgcolor: '#eef2ff', color: '#3730a3', fontWeight: 700 }} />
                    <Chip size="small" label={day.dateLabel} sx={{ height: 20, fontSize: '0.62rem', bgcolor: '#f1f5f9', color: '#334155', fontWeight: 700 }} />
                  </Stack>

                  {daySortedEvents.length === 0 ? (
                    <Box sx={{ p: 2, textAlign: 'center', bgcolor: '#fff', borderRadius: 2, border: '1px dashed', borderColor: '#cbd5e1' }}>
                      <Typography variant="body2" color="text.secondary">No images for this date</Typography>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                        gap: 1,
                      }}
                    >
                      {daySortedEvents.map((event, idx) => {
                        const isCheckIn = event.action === 'check_in';
                        const isMissingEntry = !!event.is_missing_entry;
                        const actionLabel = isMissingEntry ? (isCheckIn ? 'Missing In' : 'Missing Out') : (isCheckIn ? 'IN' : 'OUT');
                        return (
                          <Paper
                            key={`${day.dateKey}-${event.attendance_id || event.snapshot_audit_id || `${event.action}-${event.created_at}-${idx}`}`}
                            variant="outlined"
                            onMouseEnter={() => setHoveredTimelineEventKey(getTimelineEventKey(event))}
                            onMouseLeave={() => setHoveredTimelineEventKey(null)}
                            sx={{
                              borderRadius: 1.25,
                              overflow: 'hidden',
                              bgcolor: '#fff',
                              maxWidth: 170,
                              position: 'relative',
                            }}
                          >
                            <Box sx={{ position: 'relative', width: '100%', height: 120, bgcolor: '#f3f4f6' }}>
                              {event.snapshot_audit_id && !isMissingEntry ? (
                                <AuditImg
                                  auditId={event.snapshot_audit_id}
                                  alt={actionLabel}
                                  sx={{ width: '100%', height: 120, display: 'block' }}
                                  imgSx={{
                                    width: '100%',
                                    height: 120,
                                    objectFit: 'cover',
                                    objectPosition: 'center',
                                    display: 'block',
                                    cursor: 'pointer',
                                  }}
                                  onClick={() => handleImageClick(event)}
                                />
                              ) : (
                                <Box
                                  component="img"
                                  src={resolveIntrusionEventImage(event, snapshotCache, thumbnail || null)}
                                  alt={actionLabel}
                                  onClick={() => handleImageClick(event)}
                                  sx={{
                                    width: '100%',
                                    height: 120,
                                    objectFit: 'cover',
                                    objectPosition: 'center',
                                    opacity: isMissingEntry ? 0.5 : 1,
                                    cursor: isMissingEntry ? 'default' : 'pointer',
                                    display: 'block',
                                  }}
                                />
                              )}
                              {isMissingEntry && onMissingEntrySearchClick && (
                                <Tooltip title="Search Audit Logs for Missing Entry" arrow placement="top">
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onMissingEntrySearchClick(event, thumbnail || null, recent_events, person_id);
                                    }}
                                    sx={{
                                      position: 'absolute',
                                      top: 6,
                                      left: 6,
                                      zIndex: 1,
                                      bgcolor: 'rgba(255,255,255,0.92)',
                                      '&:hover': { bgcolor: '#fff' },
                                      width: 30,
                                      height: 30,
                                    }}
                                  >
                                    <SearchIcon sx={{ fontSize: 16, color: primary }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                            <Box sx={{ p: 1.1 }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  display: 'block',
                                  fontWeight: 700,
                                  color: '#111827',
                                  fontSize: '0.95rem',
                                  lineHeight: 1.15,
                                  mb: 0.9,
                                }}
                                noWrap
                              >
                                {isMissingEntry ? '--:--:--' : formatDate(event.created_at, true, false)}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block', fontSize: '0.72rem', mb: 0.75 }}
                                noWrap
                              >
                                {isMissingEntry ? '--' : formatDateOnly(event.created_at)}
                              </Typography>
                              <Chip
                                size="small"
                                label={isCheckIn ? 'IN' : 'OUT'}
                                sx={{
                                  height: 22,
                                  fontSize: '0.62rem',
                                  fontWeight: 700,
                                  borderRadius: 999,
                                  bgcolor: isMissingEntry ? '#e2e8f0' : (isCheckIn ? '#dcfce7' : '#fee2e2'),
                                  color: isMissingEntry ? '#334155' : (isCheckIn ? '#166534' : '#991b1b'),
                                }}
                              />
                            </Box>
                          </Paper>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        )}
      </Box>

      <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', bgcolor: '#fff', display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="outlined" size="small" onClick={onClose} sx={{ borderRadius: 2, textTransform: 'none', px: 3 }}>
          Close
        </Button>
      </Box>
    </Dialog>
  );
};

const IntrusionSummaryCard = ({ summary, thumbnail, snapshotCache, onAttendanceEventLogClick = null, onMissingEntrySearchClick = null }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [galleryRevision, setGalleryRevision] = useState(0);
  const { openImagePreview } = useImagePreview();
  const { person_name, person_id, employee_code, recent_events = [] } = summary;

  useEffect(() => {
    const auditIds = collectIntrusionGalleryAuditIds(recent_events);
    if (!auditIds.length) return undefined;
    const unsubs = auditIds.map((auditId) => subscribeAuditImage(auditId, () => {
      setGalleryRevision((prev) => prev + 1);
    }));
    return () => unsubs.forEach((unsub) => unsub());
  }, [recent_events]);

  const avatarLetter = (person_name || person_id || '?').charAt(0).toUpperCase();
  const sortedByTime = [...recent_events].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  const latestEvent = sortedByTime[sortedByTime.length - 1] || null;
  const isCurrentlyIn = latestEvent?.action === 'check_in';
  const firstCheckIn = sortedByTime.find((evt) => evt.action === 'check_in');
  const lastCheckOut = [...sortedByTime].reverse().find((evt) => evt.action === 'check_out');
  const previewEvents = [...recent_events].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).slice(0, 3);
  const fallbackImage = previewEvents.length > 0 ? resolveIntrusionEventImage(previewEvents[0], snapshotCache, thumbnail || null) : (thumbnail || ANONYMOUS_IMAGE);
  const capturedGalleryImages = useMemo(() => {
    void galleryRevision;
    return buildAttendanceCapturedImageGallery({ summary, snapshotCache });
  }, [summary, snapshotCache, galleryRevision]);
  const hasMultipleGalleryImages = capturedGalleryImages.length > 1;

  const handleViewImagesClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    void openIntrusionCapturedImageGallery({
      openImagePreview,
      summary,
      snapshotCache,
      thumbnail,
    });
  };

  return (
    <>
      <Card
        variant="outlined"
        onClick={() => setModalOpen(true)}
        sx={{
          borderRadius: 3,
          width: '100%',
          borderTop: `3px solid ${primary}`,
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 10px 30px rgba(15, 23, 42, 0.21)',
          cursor: 'pointer',
          transition: 'all 0.25s ease',
          '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 18px 36px rgba(15,23,42,0.16)' },
        }}
      >
        <CardContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: 1.25, sm: 1.5, md: 1.5 },
            p: { xs: 1.5, sm: 2, md: 2 },
          }}
        >
          <Stack direction="row" spacing={{ xs: 1.25, sm: 1.5 }} alignItems="center" sx={{ minWidth: 0 }}>
            <Avatar
              src={thumbnail || fallbackImage || undefined}
              sx={{
                width: { xs: 40, sm: 48, md: 48 },
                height: { xs: 40, sm: 48, md: 48 },
                bgcolor: '#EEF2FF',
                color: '#374151',
                fontWeight: 700,
                flexShrink: 0,
                fontSize: { xs: '0.9rem', sm: '1rem', md: '1rem' },
              }}
            >
              {avatarLetter}
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 700, fontSize: { xs: '0.85rem', sm: '0.9rem', md: '0.9rem' } }}
                noWrap
              >
                {person_name || 'Unknown person'}
              </Typography>
              <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                  {employee_code || person_id}
                </Typography>

              </Stack>
            </Box>
          </Stack>

          <Stack direction="row" spacing={0.5} flexWrap="wrap" alignItems="center" useFlexGap>
            {firstCheckIn && <Chip size="small" label={`In: ${formatDate(firstCheckIn.created_at, true, false)}`} sx={{ height: { xs: 22, sm: 24 }, fontSize: { xs: '0.65rem', sm: '0.75rem' } }} />}
            {lastCheckOut && <Chip size="small" label={`Out: ${formatDate(lastCheckOut.created_at, true, false)}`} sx={{ height: { xs: 22, sm: 24 }, fontSize: { xs: '0.65rem', sm: '0.75rem' } }} />}
            {hasMultipleGalleryImages && (
              <Tooltip title="Captured Images" arrow>
                <IconButton
                  size="small"
                  aria-label="Open captured image gallery"
                  onClick={handleViewImagesClick}
                  sx={{
                    ml: 'auto',
                    width: { xs: 28, sm: 30 },
                    height: { xs: 28, sm: 30 },
                    bgcolor: '#fff',
                    color: primary,
                    border: '1px solid rgba(64, 81, 138, 0.24)',
                    boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)',
                    '&:hover': {
                      bgcolor: '#f8fafc',
                      borderColor: primary,
                    },
                  }}
                >
                  <PhotoLibraryIcon sx={{ fontSize: { xs: 17, sm: 18 } }} />
                </IconButton>
              </Tooltip>
            )}
          </Stack>

          <Divider />
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Recent activity
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 1.2 }}>
            {previewEvents.map((event, idx) => {
              const image = resolveIntrusionEventImage(event, snapshotCache, thumbnail || null);
              const isCheckIn = event.action === 'check_in';
              return (
                <Paper key={event.snapshot_audit_id || `${event.created_at}-${idx}`} variant="outlined" sx={{ borderRadius: { xs: 1.5, sm: 2 }, overflow: 'hidden' }}>
                  <Box sx={{ position: 'relative', height: { xs: 80, sm: 100, md: 110 }, background: '#0f172a', flexShrink: 0 }}>
                    {event.snapshot_audit_id ? (
                      <LazyAuditImg
                        auditId={event.snapshot_audit_id}
                        alt={isCheckIn ? 'IN' : 'OUT'}
                        anonymousImage={image}
                        sx={{ width: '100%', height: { xs: 80, sm: 100, md: 110 } }}
                        imgSx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <Box
                        component="img"
                        src={image}
                        alt={isCheckIn ? 'IN' : 'OUT'}
                        sx={{ width: '100%', height: { xs: 80, sm: 100, md: 110 }, objectFit: 'cover' }}
                      />
                    )}
                    <Chip
                      size="small"
                      label={isCheckIn ? 'IN' : 'OUT'}
                      sx={{
                        position: 'absolute',
                        bottom: 6,  // or theme.spacing(0.5)
                        left: 6,
                        height: { xs: 18, sm: 20 },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        px: { xs: .5, sm: .6 },
                        fontSize: { xs: '0.58rem', sm: '0.66rem' },
                        fontWeight: 700,
                        bgcolor: isCheckIn ? '#dcfce7' : '#fee2e2',
                        color: isCheckIn ? '#166534' : '#991b1b',
                        px: 1,
                        zIndex: 2,
                        boxShadow: '0px 2px 4px rgba(16,24,40,0.04)',
                      }}
                    />
                  </Box>
           
                  <Box sx={{ p: { xs: 0.75, sm: 1 } }}>
                    <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem' } }} noWrap>
                      {formatDate(event.created_at, true, false)}
                    </Typography>
                  </Box>
                </Paper>
              );
            })}
          </Box>
        </CardContent>
      </Card>

      <IntrusionTimelineModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        summary={summary}
        thumbnail={thumbnail}
        snapshotCache={snapshotCache}
        onAttendanceEventLogClick={onAttendanceEventLogClick}
        onMissingEntrySearchClick={onMissingEntrySearchClick}
      />
    </>
  );
};

const IntrusionAttendanceGridLayout = ({ summaries, personThumbnails, snapshotCache, onAttendanceEventLogClick = null, onMissingEntrySearchClick = null }) => (
  <Grid container spacing={2.5}>
    {summaries.map((summary) => (
      <Grid item xs={12} sm={6} md={4} key={`${summary.person_id || summary.person_name || 'unknown'}-${summary.last_seen}`}>
        <IntrusionSummaryCard
          summary={summary}
          thumbnail={summary.person_id ? personThumbnails[summary.person_id] : null}
          snapshotCache={snapshotCache}
          onAttendanceEventLogClick={onAttendanceEventLogClick}
          onMissingEntrySearchClick={onMissingEntrySearchClick}
        />
      </Grid>
    ))}
  </Grid>
);

const Intrusion = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const parseDateParam = (value) => {
    if (!value) return null;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const normalizeTimeParam = (value, fallback) => {
    if (!value || typeof value !== 'string' || !value.includes(':')) return fallback;
    const [hoursRaw, minutesRaw] = value.split(':');
    const hours = Number(hoursRaw);
    const minutes = Number(minutesRaw);
    if (
      Number.isNaN(hours) ||
      Number.isNaN(minutes) ||
      hours < 0 ||
      hours > 23 ||
      minutes < 0 ||
      minutes > 59
    ) {
      return fallback;
    }
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  };

  const initialPersonName = sanitizeNameSearchInput(searchParams.get('person_name') || '');
  const initialPersonId = (searchParams.get('person_id') || '').trim();
  const rawCamera = (searchParams.get('camera') || '').toLowerCase();
  const initialCamera = rawCamera === 'in' || rawCamera === 'out' ? rawCamera : '';
  const rawSortOrder = (searchParams.get('sort_order') || '').toLowerCase();
  const initialSortOrder = rawSortOrder === 'asc' || rawSortOrder === 'desc' ? rawSortOrder : 'desc';
  const initialStartDate = parseDateParam(searchParams.get('start_date')) || new Date();
  const initialEndDate = parseDateParam(searchParams.get('end_date')) || initialStartDate;
  const initialStartTime = normalizeTimeParam(searchParams.get('start_time'), '00:00');
  const initialEndTime = normalizeTimeParam(searchParams.get('end_time'), '23:59');

  // State
  const [personName, setPersonName] = useState(initialPersonName);
  const [personNameInput, setPersonNameInput] = useState(initialPersonName);
  const [personId, setPersonId] = useState(initialPersonId);
  const [camera, setCamera] = useState(initialCamera); // '', 'in', 'out'
  const [sortOrder, setSortOrder] = useState(initialSortOrder);
  const [summaries, setSummaries] = useState([]);
  const [pagination, setPagination] = useState({ limit: 100, offset: 0, current_page: 1, total: 0 });
  const [loading, setLoading] = useState(false);
  const [availableNames, setAvailableNames] = useState([]);
  const [personThumbnails, setPersonThumbnails] = useState({});
  const personThumbnailsRef = useRef({});
  const thumbnailRequestsRef = useRef(new Set());
  const [auditSnapshots, setAuditSnapshots] = useState({});
  const auditSnapshotsRef = useRef({});
  const auditSnapshotRequestsRef = useRef(new Set());
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [exportLoading, setExportLoading] = useState(false);

  // Date Range Picker state (default to today)
  const [dateRange, setDateRange] = useState([
    {
      startDate: initialStartDate,
      endDate: initialEndDate,
      key: 'selection'
    }
  ]);
  const [datePickerAnchor, setDatePickerAnchor] = useState(null);
  const datePickerOpen = Boolean(datePickerAnchor);
  const [startTime, setStartTime] = useState(initialStartTime);
  const [endTime, setEndTime] = useState(initialEndTime);

  // Timing dialog state (shared with Audit component)
  const [timingDialog, setTimingDialog] = useState({ open: false, log: null, stack: [], detailLoading: false });
  const [missingEntrySearchModalOpen, setMissingEntrySearchModalOpen] = useState(false);
  const [missingEntrySearchProps, setMissingEntrySearchProps] = useState({
    missingEvent: null,
    personThumbnail: null,
    recentEvents: [],
    personId: null,
  });

  const show = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const applyPersonNameFilterNow = useCallback((rawValue) => {
    const sanitizedValue = sanitizeNameSearchInput(rawValue || '');
    setPersonNameInput(sanitizedValue);
    setPersonName(sanitizedValue);
  }, []);

  useEffect(() => {
    setSearchParams((prevParams) => {
      const params = new URLSearchParams(prevParams);
      if (personName) params.set('person_name', personName); else params.delete('person_name');
      if (personId) params.set('person_id', personId); else params.delete('person_id');
      if (camera) params.set('camera', camera); else params.delete('camera');
      if (sortOrder && sortOrder !== 'desc') params.set('sort_order', sortOrder); else params.delete('sort_order');
      if (dateRange[0]?.startDate) {
        params.set('start_date', format(dateRange[0].startDate, 'yyyy-MM-dd'));
      }
      if (dateRange[0]?.endDate) {
        params.set('end_date', format(dateRange[0].endDate, 'yyyy-MM-dd'));
      }
      if (startTime && startTime !== '00:00') params.set('start_time', startTime); else params.delete('start_time');
      if (endTime && endTime !== '23:59') params.set('end_time', endTime); else params.delete('end_time');
      return params.toString() !== prevParams.toString() ? params : prevParams;
    }, { replace: true });
  }, [camera, dateRange, endTime, personId, personName, sortOrder, startTime, setSearchParams]);

  const blobToDataUrl = (blob) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

  const buildDateTimeRange = useCallback((overrides = {}) => {
    const effectiveDateRange = Array.isArray(overrides.dateRange) && overrides.dateRange.length
      ? overrides.dateRange
      : dateRange;
    const effectiveStartTime = typeof overrides.startTime === 'string' ? overrides.startTime : startTime;
    const effectiveEndTime = typeof overrides.endTime === 'string' ? overrides.endTime : endTime;
    const startDate = effectiveDateRange[0]?.startDate || new Date();
    const endDate = effectiveDateRange[0]?.endDate || startDate;
    const parseTime = (timeValue, fallbackHour, fallbackMinute) => {
      if (!timeValue || typeof timeValue !== 'string' || !timeValue.includes(':')) {
        return [fallbackHour, fallbackMinute];
      }
      const [hoursRaw, minutesRaw] = timeValue.split(':');
      const hours = Number(hoursRaw);
      const minutes = Number(minutesRaw);
      if (
        Number.isNaN(hours) ||
        Number.isNaN(minutes) ||
        hours < 0 ||
        hours > 23 ||
        minutes < 0 ||
        minutes > 59
      ) {
        return [fallbackHour, fallbackMinute];
      }
      return [hours, minutes];
    };

    // Parse IST times (user input)
    const [istStartHour, istStartMinute] = parseTime(effectiveStartTime, 0, 0);
    const [istEndHour, istEndMinute] = parseTime(effectiveEndTime, 23, 59);

    // IST is UTC+5:30, so we need to subtract 5:30 from IST time to get UTC
    // Create date objects representing IST time, then convert to UTC
    const startDateIST = new Date(Date.UTC(
      startDate.getFullYear(),
      startDate.getMonth(),
      startDate.getDate(),
      istStartHour,
      istStartMinute,
      0
    ));
    // Subtract 5 hours 30 minutes (IST offset) to convert to UTC
    const startDateTimeUTC = new Date(startDateIST.getTime() - (5 * 60 + 30) * 60 * 1000);

    const endDateIST = new Date(Date.UTC(
      endDate.getFullYear(),
      endDate.getMonth(),
      endDate.getDate(),
      istEndHour,
      istEndMinute,
      59
    ));
    // Subtract 5 hours 30 minutes (IST offset) to convert to UTC
    const endDateTimeUTC = new Date(endDateIST.getTime() - (5 * 60 + 30) * 60 * 1000);

    const formattedStart = format(startDateTimeUTC, "yyyy-MM-dd'T'HH:mm");
    const formattedEnd = format(endDateTimeUTC, "yyyy-MM-dd'T'HH:mm");
    return { formattedStart, formattedEnd };
  }, [dateRange, startTime, endTime]);

  // Transform audit logs to summary format
  const transformAuditLogsToSummaries = useCallback((auditLogs, filters = {}) => {
    const { cameraFilter, sortOrder: sortOrderParam } = filters;
    
    // Group audit logs by person_id or recognized_person_id
    const personGroups = {};
    
    auditLogs.forEach((log) => {
      const personId = log.recognized_person_id || log.person_id;
      // For unknown persons, use a combination of name and camera as identifier
      const groupKey = personId || `unknown_${log.recognized_name || 'Unknown'}_${log.camera || ''}`;
      const personName = log.recognized_name || 'Unknown';
      
      if (!personGroups[groupKey]) {
        personGroups[groupKey] = {
          person_id: personId || groupKey,
          person_name: personName,
          employee_code: log.employee_code || '',
          logs: [],
        };
      }
      personGroups[groupKey].logs.push(log);
      // Update name if we get a more recent name for the same person
      if (log.recognized_name && log.recognized_name !== 'Unknown') {
        personGroups[groupKey].person_name = log.recognized_name;
      }
    });

    // Transform each group into a summary
    const summaries = Object.values(personGroups).map((group) => {
      const logs = group.logs.sort((a, b) => {
        const dateA = new Date(a.created_at || a.captured_at || 0);
        const dateB = new Date(b.created_at || b.captured_at || 0);
        return sortOrderParam === 'asc' ? dateA - dateB : dateB - dateA;
      });

      // Determine action from camera direction
      const getActionFromCamera = (camera) => {
        if (!camera) return 'check_in'; // Default
        const cameraUpper = String(camera).toUpperCase();
        if (cameraUpper.includes('OUT') || cameraUpper === 'OUT') {
          return 'check_out';
        }
        return 'check_in';
      };

      // Transform logs to recent_events format
      const recent_events = logs.map((log) => {
        const camera = log.camera || '';
        const action = getActionFromCamera(camera);
        
          return {
          action,
          created_at: log.created_at || log.captured_at || log.event_timestamp || new Date().toISOString(),
          snapshot_audit_id: log.audit_id,
          image_available: true, // Audit logs typically have images
          recognition_audit_log: log,
          is_manual: false,
        };
      });

      // Calculate stats
      const checkIns = recent_events.filter((e) => e.action === 'check_in').length;
      const checkOuts = recent_events.filter((e) => e.action === 'check_out').length;
      const net_flow = checkIns - checkOuts;

      // Get first and last seen
      const sortedByTime = [...recent_events].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      const first_seen = sortedByTime[0]?.created_at || null;
      const last_seen = sortedByTime[sortedByTime.length - 1]?.created_at || null;

      // Determine if currently in premises (last event is check_in)
      const lastEvent = sortedByTime[sortedByTime.length - 1];
      const isCurrentlyIn = lastEvent?.action === 'check_in';

      return {
        person_id: group.person_id,
        person_name: group.person_name,
        employee_code: group.employee_code,
        total_check_ins: checkIns,
        total_check_outs: checkOuts,
        net_flow,
        recent_events,
        first_seen,
        last_seen,
        imbalance_flag: Math.abs(net_flow) > 0, // Flag if there's an imbalance
        isCurrentlyIn,
      };
    });

    // Apply filters
    let filtered = summaries;

    // Filter by camera if specified
    if (cameraFilter) {
      filtered = filtered.filter((summary) => {
        const events = summary.recent_events || [];
        return events.some((e) => {
          const log = e.recognition_audit_log;
          if (!log) return false;
          const logCamera = (log.camera || '').toLowerCase();
          const filterCamera = cameraFilter.toLowerCase();
          return logCamera === filterCamera || 
                 (filterCamera === 'in' && logCamera.includes('in')) ||
                 (filterCamera === 'out' && logCamera.includes('out'));
        });
      });
    }

    // Sort by last_seen
    filtered.sort((a, b) => {
      const dateA = new Date(a.last_seen || 0);
      const dateB = new Date(b.last_seen || 0);
      return sortOrderParam === 'asc' ? dateA - dateB : dateB - dateA;
    });

    return filtered;
  }, []);

  const loadSummary = useCallback(async (limit, offset, filterOverrides = {}) => {
    try {
      setLoading(true);
      const { formattedStart, formattedEnd } = buildDateTimeRange(filterOverrides);
      const effectivePersonName = filterOverrides.personName !== undefined
        ? filterOverrides.personName
        : personName;
      const effectivePersonId = filterOverrides.personId !== undefined
        ? filterOverrides.personId
        : personId;
      const effectiveCamera = filterOverrides.camera !== undefined
        ? filterOverrides.camera
        : camera;
      const effectiveSortOrder = filterOverrides.sortOrder !== undefined
        ? filterOverrides.sortOrder
        : sortOrder;

      // Fetch audit logs with is_intrusion=true
      // Note: We fetch a larger batch since we need to group by person before paginating
      const res = await getAuditLogs({
        name: (effectivePersonName || '').trim(),
        person_id: (effectivePersonId || '').trim() || undefined,
        camera: effectiveCamera || undefined,
        start_time: formattedStart,
        end_time: formattedEnd,
        is_intrusion: true, // Only get intrusion audit logs
        limit: Math.max(limit * 50, 500), // Fetch more logs to account for grouping (min 500)
        offset: 0, // We'll handle pagination after grouping
      });

      const auditLogs = res.logs || res || [];
      const totalLogs = res.pagination?.total || res.total || auditLogs.length;

      // Transform audit logs to summaries
      const allSummaries = transformAuditLogsToSummaries(auditLogs, {
        cameraFilter: effectiveCamera,
        sortOrder: effectiveSortOrder,
      });

      // Apply pagination to summaries
      const paginatedSummaries = allSummaries.slice(offset, offset + limit);
      const total = allSummaries.length;

      setSummaries(paginatedSummaries);
      setPagination({ limit, offset, current_page: Math.floor(offset / limit) + 1, total });
    } catch (e) {
      console.error(e);
      show('Failed to load intrusion audit logs', 'error');
    } finally {
      setLoading(false);
    }
  }, [personName, personId, camera, sortOrder, buildDateTimeRange, transformAuditLogsToSummaries]);

  // Load unique names for autocomplete
  useEffect(() => {
    const loadNames = async () => {
      try {

        const res = await getAllPersonNames();
        const names = res?.map((d)=>`${d.name} (${d.person_id})`) || [];

        names.sort((a, b) => a.localeCompare(b));
        setAvailableNames(names);
      } catch (e) {
        console.error('Failed to load unique names:', e);
      }
    };
    loadNames();
  }, []);


  // Handle navigation state from Dashboard (if needed in future)
  // Note: Intrusion page now uses audit log filters, not attendance filters

  // Load summary on mount and when filters change
  useEffect(() => {
    loadSummary(pagination.limit, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personName, camera, sortOrder]);

  // Load thumbnails for summaries
  useEffect(() => {
    if (!Array.isArray(summaries) || summaries.length === 0) {
      return;
    }
    summaries.forEach((summary) => {
      const pid = summary.person_id;
      if (!pid) return;
      if (personThumbnailsRef.current[pid] !== undefined || thumbnailRequestsRef.current.has(pid)) {
        return;
      }
      thumbnailRequestsRef.current.add(pid);
      (async () => {
        try {
          const faces = await getPersonFaces(pid);
          const faceList = Array.isArray(faces) ? faces : [];
          const preferredFace = faceList.find((face) => !face.deleted_at) || faceList[0];
          if (preferredFace) {
            const blob = await getFaceImage(preferredFace.face_id);
            const dataUrl = await blobToDataUrl(blob);
            setPersonThumbnails((prev) => ({ ...prev, [pid]: dataUrl }));
          } else {
            setPersonThumbnails((prev) => ({ ...prev, [pid]: null }));
          }
        } catch (err) {
          console.error('Failed to load intrusion thumbnail:', err);
          setPersonThumbnails((prev) => ({ ...prev, [pid]: null }));
        } finally {
          thumbnailRequestsRef.current.delete(pid);
        }
      })();
    });
  }, [summaries]);

  const openTimingDialog = async ({ log, stack = [] }) => {
    const auditId = log?.audit_id;
    setTimingDialog({ open: true, log, stack, detailLoading: Boolean(auditId) });
    if (!auditId) return;
    try {
      const next = await hydrateTimingDialogFromDetail(log, stack);
      setTimingDialog((prev) => {
        if (!prev.open || String(prev.log?.audit_id || '') !== String(auditId)) return prev;
        return { open: true, ...next };
      });
    } catch (e) {
      console.error('Failed to load audit log detail:', e);
      setTimingDialog((prev) => (
        prev.open && String(prev.log?.audit_id || '') === String(auditId)
          ? { ...prev, detailLoading: false }
          : prev
      ));
    }
  };

  const handleOpenMissingEntrySearch = (missingEvent, personThumbnail, recentEvents, personId) => {
    if (!missingEvent?.is_missing_entry) {
      show('Invalid missing entry data', 'error');
      return;
    }
    setMissingEntrySearchProps({ missingEvent, personThumbnail, recentEvents, personId });
    setMissingEntrySearchModalOpen(true);
  };

  const handleCloseMissingEntrySearch = () => {
    setMissingEntrySearchModalOpen(false);
    setMissingEntrySearchProps({ missingEvent: null, personThumbnail: null, recentEvents: [], personId: null });
  };

  const closeTimingDialog = () => {
    setTimingDialog({ open: false, log: null, stack: [], detailLoading: false });
  };

  // const handleExportDialog = async () => {
  //   try {
  //     setExportLoading(true);
  //     const { formattedStart, formattedEnd } = buildDateTimeRange();
      
  //     const res = await exportAuditLogs({
  //       name: personName.trim(),
  //       camera: camera || undefined,
  //       start_time: formattedStart,
  //       end_time: formattedEnd,
  //       is_intrusion: true,
  //     });
      
  //     if (res.download_url) {
  //       window.open(res.download_url, '_blank');
  //       show('Export started successfully', 'success');
  //     } else {
  //       show('Export completed', 'success');
  //     }
  //   } catch (e) {
  //     console.error(e);
  //     show('Failed to export audit logs', 'error');
  //   } finally {
  //     setExportLoading(false);
  //   }
  // };

  // Filtering is now handled in transformAuditLogsToSummaries
  const filteredSummaries = summaries;

  const hasActiveFilters = useMemo(() => (
    !!(personName?.trim()) ||
    !!personId ||
    !!camera ||
    sortOrder !== 'desc' ||
    !isDefaultDateTimeRange(dateRange, startTime, endTime)
  ), [camera, dateRange, endTime, personId, personName, sortOrder, startTime]);

  const handleClearAllFilters = useCallback(() => {
    const todayRange = createTodayDateRange();
    setPersonName('');
    setPersonNameInput('');
    setPersonId('');
    setCamera('');
    setSortOrder('desc');
    setDateRange(todayRange);
    setStartTime(DEFAULT_START_TIME);
    setEndTime(DEFAULT_END_TIME);
    setDatePickerAnchor(null);
    loadSummary(pagination.limit, 0, {
      dateRange: todayRange,
      startTime: DEFAULT_START_TIME,
      endTime: DEFAULT_END_TIME,
      personName: '',
      personId: '',
      camera: '',
      sortOrder: 'desc',
    });
  }, [loadSummary, pagination.limit]);

  return (
    <Container maxWidth="2200" disableGutters sx={{ px: 0 }}>
      <PageHeader
        title="Intrusion"
        subtitle="Track intrusion events"
        icon={null}
      />

      {/* Filters Section */}
      <Box sx={pageFiltersToolbarSx}>
        <Stack {...pageFiltersStackSx}>
          <Box sx={pageFiltersControlsSx}>
            <Autocomplete
              size="small"
              freeSolo
              options={availableNames}
              value={personNameInput || null}
              inputValue={personNameInput || ''}
              onInputChange={(e, newValue) => setPersonNameInput(sanitizeNameSearchInput(newValue || ''))}
              onChange={(e, newValue) => applyPersonNameFilterNow(newValue || '')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search by Name or person id"
                  onKeyDown={(e) => {
                    if (typeof params?.inputProps?.onKeyDown === 'function') {
                      params.inputProps.onKeyDown(e);
                    }
                    if (e.key !== 'Enter') return;
                    applyPersonNameFilterNow(e.currentTarget.value || '');
                  }}
                />
              )}
              sx={pageFilterAutocompleteSx(260)}
            />

              <FormControl
                size="small"
                sx={pageFilterFormControlSx(210)}
              >
                <InputLabel id="camera-label" shrink>
                  Select Camera Type
                </InputLabel>
                <Select
                  labelId="camera-label"
                  value={camera}
                  label="Select Camera Type"
                  onChange={(e) => setCamera(e.target.value)}
                  displayEmpty
                  renderValue={(v) => v ? (v === 'in' ? 'Camera: In' : 'Camera: Out') : 'All'}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="in">Camera: In</MenuItem>
                  <MenuItem value="out">Camera: Out</MenuItem>
                </Select>
              </FormControl>

              <FormControl
                size="small"
                sx={pageFilterFormControlSx(160)}
              >
                <InputLabel id="sort-order-label" shrink>
                  Sort By 
                </InputLabel>
                <Select
                  labelId="sort-order-label"
                  value={sortOrder}
                  label="Sort By"
                  onChange={(e) => setSortOrder(e.target.value)}
                  displayEmpty
                  renderValue={(v) => (v === 'asc' ? 'Time : Earliest to Latest' : ' Time : Latest to Earliest')}
                >
                  <MenuItem value="desc">Time : Latest to Earliest</MenuItem>
                  <MenuItem value="asc">Time : Earliest to Latest</MenuItem>
                </Select>
              </FormControl>

            <Box
              onClick={(e) => setDatePickerAnchor(e.currentTarget)}
              sx={pageFilterDatePickerSx(180)}
            >
              <CalendarMonth fontSize="small" color="action" />
              <Typography variant="body2" sx={{ flex: 1, fontSize: '0.875rem' }}>
                {isSameDay(dateRange[0].startDate, dateRange[0].endDate)
                  ? format(dateRange[0].startDate, 'MMM dd, yyyy')
                  : `${format(dateRange[0].startDate, 'MMM dd')} - ${format(dateRange[0].endDate, 'MMM dd, yyyy')}`}
              </Typography>
            </Box>
            <Popover
              open={datePickerOpen}
              anchorEl={datePickerAnchor}
              onClose={() => setDatePickerAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              slotProps={{
                paper: {
                  sx: { maxWidth: '100vw', overflow: 'auto' }
                }
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
                {/* Quick Select Presets */}
                <Box sx={{
                  p: 2,
                  borderRight: { md: '1px solid' },
                  borderBottom: { xs: '1px solid', md: 'none' },
                  borderColor: 'divider',
                  minWidth: 140,
                }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Quick Select
                  </Typography>
                  <Stack spacing={0.5}>
                    {[
                      { label: 'Today', getValue: () => ({ startDate: new Date(), endDate: new Date() }) },
                      { label: 'Yesterday', getValue: () => ({ startDate: addDays(new Date(), -1), endDate: addDays(new Date(), -1) }) },
                      { label: 'Last 7 Days', getValue: () => ({ startDate: addDays(new Date(), -6), endDate: new Date() }) },
                      { label: 'Last 30 Days', getValue: () => ({ startDate: addDays(new Date(), -29), endDate: new Date() }) },
                      { label: 'This Month', getValue: () => {
                        const now = new Date();
                        return { startDate: new Date(now.getFullYear(), now.getMonth(), 1), endDate: now };
                      }},
                      { label: 'Last Month', getValue: () => {
                        const now = new Date();
                        return { startDate: new Date(now.getFullYear(), now.getMonth() - 1, 1), endDate: new Date(now.getFullYear(), now.getMonth(), 0) };
                      }},
                    ].map((preset) => (
                      <Button
                        key={preset.label}
                        size="small"
                        variant="text"
                        onClick={() => {
                          const { startDate, endDate } = preset.getValue();
                          setDateRange([{ startDate, endDate, key: 'selection' }]);
                        }}
                        sx={{
                          justifyContent: 'flex-start',
                          textTransform: 'none',
                          px: 1.5,
                          py: 0.75,
                          color: 'text.primary',
                          '&:hover': { bgcolor: 'action.hover' }
                        }}
                      >
                        {preset.label}
                      </Button>
                    ))}
                  </Stack>
                </Box>

                {/* Date Range Picker - Responsive */}
                <Box sx={{
                  '& .rdrCalendarWrapper': {
                    fontSize: { xs: '0.8rem', sm: '0.875rem' }
                  },
                  '& .rdrMonth': {
                    width: { xs: '280px', sm: '300px' },
                  },
                }}>
                  <DateRangePicker
                    ranges={dateRange}
                    onChange={(ranges) => setDateRange([ranges.selection])}
                    moveRangeOnFirstSelection={false}
                    months={isMobile ? 1 : 2}
                    direction={isMobile ? 'vertical' : 'horizontal'}
                    rangeColors={[primary]}
                    staticRanges={[]}
                    inputRanges={[]}
                    maxDate={new Date()}
                  />
                  <Box sx={{ px: 2, pb: 2, pt: 1, display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
                    <TextField
                      label="Start Time"
                      type="time"
                      size="small"
                      value={startTime}
                      onChange={(e) => setStartTime(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ step: 60 }}
                      fullWidth
                    />
                    <TextField
                      label="End Time"
                      type="time"
                      size="small"
                      value={endTime}
                      onChange={(e) => setEndTime(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ step: 60 }}
                      fullWidth
                    />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', alignItems: 'center', p: 2, pt: 0 }}>
                    <Button
                      onClick={() => {
                        const today = new Date();
                        setDateRange([{ startDate: today, endDate: today, key: 'selection' }]);
                        setStartTime('00:00');
                        setEndTime('23:59');
                      }}
                      variant="outlined"
                      size="small"
                      startIcon={<RefreshIcon />}
                    >
                      Reset
                    </Button>
                    <Button onClick={() => setDatePickerAnchor(null)} variant="outlined" size="small">
                      Cancel
                    </Button>
                    <Button
                      onClick={() => {
                        setDatePickerAnchor(null);
                        loadSummary(pagination.limit, 0);
                      }}
                      variant="contained"
                      size="small"
                    >
                      Apply
                    </Button>
                  </Box>
                </Box>
              </Box>
            </Popover>

            <ClearAllFiltersButton
              onClear={handleClearAllFilters}
              disabled={!hasActiveFilters}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            />
          </Box>

          {/* Action Buttons Group */}
          {/* <Box sx={{ 
            display: 'flex', 
            gap: 0.75, 
            flexWrap: 'wrap',
            alignItems: 'center'
          }}>

            <Tooltip title="Export to CSV">
              <Button
                size="small"
                variant="outlined"
                startIcon={<FileDownload />}
                onClick={handleExportDialog}
                disabled={exportLoading || loading}
                sx={{ 
                  textTransform: 'none', 
                  height: 36,
                  width: { xs: '100%', md: 'auto' },
                  minWidth: 'auto',
                  px: 1.5,
                  fontSize: '0.875rem'
                }}
              >
                Export CSV
              </Button>
            </Tooltip>
          </Box> */}
        </Stack>
      </Box>

      {/* Content */}
      <Box sx={{ minHeight: 200 }}>
        {loading ? (
          <Grid container spacing={2.5}>
            {Array.from({ length: 6 }).map((_, idx) => (
              <Grid item xs={12} sm={6} md={4} key={`intrusion-skel-${idx}`}>
                <AttendanceCardSkeleton />
              </Grid>
            ))}
          </Grid>
        ) : (
          filteredSummaries.length === 0 ? (
            <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 2, background: '#fafafa' }}>
              <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>No intrusion events found</Typography>
              <Typography variant="body2" color="text.secondary">
                {camera
                  ? `No intrusion events found with the selected camera filter.`
                  : 'Adjust filters or widen the day range to see events.'}
              </Typography>
            </Paper>
          ) : (
            <IntrusionAttendanceGridLayout
              summaries={filteredSummaries}
              personThumbnails={personThumbnails}
              snapshotCache={auditSnapshots}
              onAttendanceEventLogClick={({ log, image }) =>
                openTimingDialog({
                  log,
                  stack: [{ log, image }],
                })
              }
              onMissingEntrySearchClick={handleOpenMissingEntrySearch}
            />
          )
        )}
      </Box>

      {/* Pagination */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, flexWrap: 'wrap', gap: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="body2" color="text.secondary">
            Showing {pagination.total === 0 ? 0 : pagination.offset + 1}-{Math.min(pagination.offset + pagination.limit, pagination.total)} of {pagination.total} people
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">Cards per page:</Typography>
            <Select
              size="small"
              value={pagination.limit}
              onChange={(e) => loadSummary(e.target.value, 0)}
              sx={{ minWidth: 90 }}
            >
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={75}>75</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </Select>
          </Stack>
        </Stack>
        <Pagination
          count={Math.max(1, Math.ceil((pagination.total || 0) / pagination.limit))}
          page={pagination.current_page}
          onChange={(e, page) => {
            const newOffset = (page - 1) * pagination.limit;
            loadSummary(pagination.limit, newOffset);
          }}
          showFirstButton
          showLastButton
        />
      </Box>

      <MissingEntrySearchModal
        open={missingEntrySearchModalOpen}
        missingEvent={missingEntrySearchProps.missingEvent}
        personThumbnail={missingEntrySearchProps.personThumbnail}
        recentEvents={missingEntrySearchProps.recentEvents}
        personId={missingEntrySearchProps.personId}
        onClose={handleCloseMissingEntrySearch}
        onSuccess={() => loadSummary(pagination.limit, pagination.offset)}
        showMessage={show}
        onLogClick={({ log, stack }) => {
          const image = Array.isArray(stack) && stack[0] ? stack[0].image : null;
          openTimingDialog({ log, stack: [{ log, image }] });
        }}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Intrusion;
